import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { canManageJobs } from '../utils/permissions';
import { getErrorMessage } from '../utils/apiError';
import { invoiceDate, invoiceMoney } from './invoiceUi';
import { invoicePaymentSummary, isDefinitePaymentRejection, localDateOnly } from './paymentUi';
import { inputClass, labelClass, primaryButtonClass } from '../components/formStyles';
import { Toast } from '../components/Toast';

export function PaymentCreatePage() {
  const api = useMemo(() => createAPIClient(), []);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invoiceId = params.get('invoiceId');
  const { user } = useAuth();
  const [invoice, setInvoice] = useState<types.Invoice | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(localDateOnly);
  const [method, setMethod] = useState<types.PaymentMethod>(types.PaymentMethod.CHECK);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [memo, setMemo] = useState('');
  const [loading, setLoading] = useState(Boolean(invoiceId));
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!invoiceId) return;
    let active = true;
    setLoading(true);
    api.getInvoice(invoiceId)
      .then(async (current) => {
        const summary = await invoicePaymentSummary(api, invoiceId, current.total);
        if (active) {
          setInvoice(current);
          setRemaining(summary.remaining);
          setAmount(summary.remaining.toFixed(2));
        }
      })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load invoice')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, invoiceId]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!invoice || busy || uncertain) return;
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0 || !/^\d+(?:\.\d{1,2})?$/.test(amount)) {
      setError('Enter an amount greater than zero with at most two decimal places.'); return;
    }
    if (!paymentDate) { setError('Choose a payment date.'); return; }
    setBusy(true); setError(null);
    try {
      const payment = await api.recordPayment({ billingCustomerId: invoice.billingCustomerId,
        invoiceIds: [invoice.id], amount, paymentMethod: method, paymentDate,
        referenceNumber: referenceNumber.trim() || undefined, memo: memo.trim() || undefined });
      navigate(`/payments/${payment.id}`, { replace: true });
    } catch (cause) {
      if (isDefinitePaymentRejection(cause)) {
        setError(getErrorMessage(cause, 'Payment was rejected. Check the fields and try again.'));
      } else {
        setUncertain(true);
        setError(`${getErrorMessage(cause, 'Could not record payment')} The request may have completed. Review the invoice's payments before starting another payment.`);
      }
    } finally { setBusy(false); }
  }

  if (!canManageJobs(user)) return <main className="max-w-3xl mx-auto px-4 py-6 sm:p-6"><p role="alert">Only office staff can record payments.</p></main>;
  if (!invoiceId) return <main className="max-w-3xl mx-auto px-4 py-6 sm:p-6"><h1 className="text-2xl font-bold mb-4">Record payment</h1><p>Open a posted or partially paid invoice to record a payment.</p><Link className="text-blue-700 underline" to="/invoices">View invoices</Link></main>;
  if (loading) return <main className="max-w-3xl mx-auto px-4 py-6 sm:p-6">Loading invoice…</main>;
  if (!invoice) return <main className="max-w-3xl mx-auto px-4 py-6 sm:p-6">{error && <Toast kind="error">{error}</Toast>}<p className="text-gray-700">{error ? 'The invoice could not be loaded.' : 'Invoice not found'}</p><Link className="text-blue-700" to="/invoices">Invoices</Link></main>;
  const eligible = [types.InvoiceStatus.POSTED, types.InvoiceStatus.PARTIAL].includes(invoice.status) && (remaining === null || remaining > 0);
  return <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
    <Link className="inline-block py-2 sm:py-0 text-blue-700 hover:underline" to={`/invoices/${invoice.id}`}>← Invoice #{invoice.invoiceNumber}</Link>
    <h1 className="text-2xl font-bold my-5">Record payment</h1>
    {error && <Toast kind="error" id={error}>{error} <Link className="underline" to={`/payments?invoiceId=${invoice.id}`}>Review payments</Link></Toast>}
    <section className="bg-white rounded-lg shadow p-4 sm:p-5 mb-5 break-words"><h2 className="font-semibold mb-2">Invoice #{invoice.invoiceNumber}</h2><p>Billing customer: {invoice.billingCustomer?.name || invoice.billingCustomerId}</p><p>Total: {invoiceMoney(invoice.total)}</p><p>Remaining: {remaining === null ? 'Unavailable' : invoiceMoney(remaining)}</p><p>Due: {invoiceDate(invoice.dueDate)}</p></section>
    {!eligible ? <p role="alert" className="text-amber-800">Payments can only be applied to posted or partially paid invoices with an open balance.</p> : <form onSubmit={submit} className="bg-white rounded-lg shadow p-4 sm:p-5 space-y-4">
      <div><label htmlFor="payment-amount" className={labelClass}>Amount</label><input id="payment-amount" type="number" min="0.01" step="0.01" required className={inputClass} value={amount} onChange={(event) => setAmount(event.target.value)} /></div>
      <div><label htmlFor="payment-date" className={labelClass}>Payment date</label><input id="payment-date" type="date" required className={inputClass} value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} /></div>
      <div><label htmlFor="payment-method" className={labelClass}>Method</label><select id="payment-method" className={inputClass} value={method} onChange={(event) => setMethod(event.target.value as types.PaymentMethod)}>{Object.values(types.PaymentMethod).map((value) => <option key={value} value={value}>{value.replace('_', ' ')}</option>)}</select></div>
      <div><label htmlFor="payment-reference" className={labelClass}>Reference number (optional)</label><input id="payment-reference" className={inputClass} value={referenceNumber} onChange={(event) => setReferenceNumber(event.target.value)} /></div>
      <div><label htmlFor="payment-memo" className={labelClass}>Memo (optional)</label><textarea id="payment-memo" className={inputClass} value={memo} onChange={(event) => setMemo(event.target.value)} /></div>
      <p className="text-sm text-gray-600">The payment will be applied to this invoice. Any excess becomes unapplied customer credit.</p>
      <button type="submit" className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`} disabled={busy || uncertain}>{busy ? 'Recording…' : uncertain ? 'Review payments before retrying' : 'Record payment'}</button>
    </form>}
  </main>;
}
